// src/utils/customerDisplay.js
//
// Shared helpers for rendering and searching customers in the web app.
//
// Customer documents in Firestore (organizations/{orgId}/customers) store
// firstName, lastName, and company as separate fields — there is NO top-level
// `name` field on most records. Older code paths assumed `customer.name`
// existed and silently failed on records without it (most commonly: customers
// created via the mobile app).
//
// All web customer-list UI should pass customers through buildCustomerDisplayName
// before rendering the name, and use customerMatchesQuery for search filtering.
// The pattern matches what the mobile JobBuilder picker uses, so search behavior
// is consistent across web and mobile.

// Build a display name from a customer doc. Handles three shapes:
//   • Person only          → "Bob Smith"
//   • Company only         → "Acme Inc"
//   • Person + Company     → "Bob Smith — Acme Inc"
// Falls back to email/phone/empty string if nothing usable exists.
const buildCustomerDisplayName = (c) => {
    if (!c) return '';
    const person = [c.firstName, c.lastName].filter(Boolean).join(' ').trim();
    const company = (c.company || '').trim();
    if (person && company) return `${person} — ${company}`;
    if (person) return person;
    if (company) return company;
    return (c.email || c.phone || c.cell_phone || '').trim();
};

// Token-AND search: every whitespace-separated token in the query must appear
// somewhere in the customer's combined searchable fields. Lets users find
// "bob smith acme" by any combination of those tokens, and disambiguate
// between two "Bob Smith" customers (one personal, one tied to Acme).
//
// custpick-8e5a2c71 — `name` added to the haystack. The rule this enforces is
// that a user must be able to find a customer by typing WHAT THE ROW SHOWS.
// Web-created records carry a top-level `name` (CustomerFormModal derives it
// from person/company), and every list that renders customers falls back to it,
// so a record whose only identifying field is `name` — an imported one, say —
// rendered perfectly and then matched nothing. Searched here, in the one place
// matching lives, rather than forked into any individual picker.
//
// This only ever ADDS matches: token-AND and case-insensitive substring are
// unchanged, and a longer haystack can never make a previously-matching
// customer stop matching.
const customerMatchesQuery = (c, query) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    const haystack = [
        c.name,
        c.firstName,
        c.lastName,
        c.company,
        c.address1,
        c.city,
        c.email,
        c.phone,
        c.cell_phone,
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
    return q.split(/\s+/).every(tok => haystack.includes(tok));
};

// Comparator for sorting customers by display name.
// Customers with an empty display name (no firstName, lastName, or company)
// always sort to the bottom regardless of ascending/descending direction —
// they're effectively "Unnamed" and should never lead the list.
//
// Caller passes the direction; this returns the correctly-signed comparison.
const compareCustomersByDisplayName = (a, b, direction = 'ascending') => {
    const aName = a.displayName || buildCustomerDisplayName(a);
    const bName = b.displayName || buildCustomerDisplayName(b);
    const aEmpty = !aName;
    const bEmpty = !bName;
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;   // a after b regardless of direction
    if (bEmpty) return -1;  // b after a regardless of direction
    const cmp = aName.toLowerCase().localeCompare(bName.toLowerCase());
    return direction === 'ascending' ? cmp : -cmp;
};

// webcustname-7c4a1e92 — the web's customer lists used orderBy('name'), and Firestore LEAVES OUT
// every record without that field. The phone's new-customer form never wrote `name` (fixed in phone
// update 3), so 20 customers were missing from the web customer list and every picker (production
// 2026-10-15: Chad's Tree Service 13, McLeod 3, Quality Irrigation 2, Matt Campos 1, test 1).
// The lists now load every customer and sort here. A record without `name` gets it filled IN MEMORY
// with the rule CustomerFormModal saves (person name, else company), so everything that reads
// customer.name shows it. Nothing here writes to the database.
const withCustomerName = (c) => {
    if (!c || (typeof c.name === 'string' && c.name.trim())) return c;
    const person = `${(c.firstName || '').trim()} ${(c.lastName || '').trim()}`.trim();
    return { ...c, name: person || (c.company || '').trim() };
};
// The order orderBy('name') gave, without case mattering, then id so ties never move.
const compareCustomersByName = (a, b) =>
    String(a.name || '').localeCompare(String(b.name || ''), 'en', { sensitivity: 'base' })
    || String(a.id || '').localeCompare(String(b.id || ''));

export {
    buildCustomerDisplayName,
    customerMatchesQuery,
    compareCustomersByDisplayName,
    withCustomerName,
    compareCustomersByName,
};